// screens/AgreementsList.tsx — K3: every Agreement, across every Account.
//
// URL-only for now (`/clients/agreements`) — there is no Account spread in
// this worktree yet to link it from (K1, a sibling chunk, builds that; once
// it lands its own Agreements pane replaces this screen's reason for
// existing as a stand-alone destination and links here per-account instead).
// See ScreenReachability.test.ts's REACHED_OTHERWISE entry for this screen.
//
// Since there is no single "every agreement, whatever Account" door yet
// either (listAgreements requires an accountId or engagementId — c4's own
// Core signature), this screen reads the Account list first (GET
// /api/engagements, presented as "Account" per the naming rule) and then
// asks for each Account's agreements — the same "load the small list, then
// fan out" shape ClientBuckets uses for its own bucket contents.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  createAgreement,
  listAccountsForPicker,
  listAgreements,
  type AccountPickerApi,
  type AgreementApi,
} from '../lib/api';
import { AGREEMENT_KINDS, AGREEMENT_SIGNATURE_MODES } from '@vowric/shared';
import './AgreementsList.css';

export interface AgreementsListProps {
  tenantId: string;
  onOpen: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready' };

interface AccountGroup {
  account: AccountPickerApi;
  agreements: AgreementApi[];
}

function statusWord(status: string): string {
  // The machine voice: the status IS the word, never a caption around it.
  return status;
}

export function AgreementsList({ tenantId, onOpen }: AgreementsListProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [accounts, setAccounts] = useState<AccountPickerApi[]>([]);
  const [creating, setCreating] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [kind, setKind] = useState<string>(AGREEMENT_KINDS[0]!);
  const [title, setTitle] = useState('');
  const [endCondition, setEndCondition] = useState('');
  const [signatureMode, setSignatureMode] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  function refresh() {
    setLoad({ phase: 'loading' });
    return listAccountsForPicker(tenantId)
      .then(async (accts) => {
        setAccounts(accts);
        const rows = await Promise.all(
          accts.map((a) => listAgreements(tenantId, { accountId: a.id }).catch(() => [] as AgreementApi[]))
        );
        setGroups(
          accts
            .map((a, i) => ({ account: a, agreements: rows[i] ?? [] }))
            .filter((g) => g.agreements.length > 0)
        );
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the agreements could not be read',
        });
      });
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function submitNew() {
    if (!accountId || !title.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      const { agreement } = await createAgreement(tenantId, {
        accountId,
        kind,
        title: title.trim(),
        endCondition: endCondition.trim() || null,
        signatureMode: signatureMode || null,
      });
      setCreating(false);
      setTitle('');
      setEndCondition('');
      setSignatureMode('');
      await refresh();
      onOpen(agreement.id);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the agreement was not created');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="agrlist">
      <div class="agrlist__eyebrow">Agreements</div>

      {load.phase === 'loading' ? (
        <div class="agrlist__skeleton" aria-hidden="true">
          <div class="agrlist__skeleton-row" />
          <div class="agrlist__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="agrlist__say">
          The agreements could not be read.
          <br />
          <span class="agrlist__say-soft">{load.message}</span>
        </div>
      ) : groups.length === 0 ? (
        <div class="agrlist__say">
          No agreements yet.
          <br />
          <span class="agrlist__say-soft">Start one below.</span>
        </div>
      ) : (
        <div class="agrlist__list">
          {groups.map((g) => (
            <section class="agrlist__group" key={g.account.id}>
              <div class="agrlist__account">{g.account.name}</div>
              {g.agreements.map((a) => (
                <button type="button" class="agrlist__row" key={a.id} onClick={() => onOpen(a.id)}>
                  <span class="agrlist__row-top">
                    <span class="agrlist__row-name">{a.title}</span>
                    <span class={`agrlist__chip agrlist__chip--${a.status}`}>{statusWord(a.status)}</span>
                  </span>
                  <span class="agrlist__row-sub">
                    {a.kind.replace(/_/g, ' ')} · v{a.version}
                    {a.acceptedOn ? ` · accepted ${a.acceptedOn}` : ''}
                    {a.offeredRevision ? ` · offered rev ${a.offeredRevision}` : ''}
                    {a.acceptedRevision ? ` · accepted rev ${a.acceptedRevision}` : ''}
                    {a.effectiveFrom ? ` · effective from ${a.effectiveFrom}${a.effectiveUntil ? ` until ${a.effectiveUntil}` : ''}` : ''}
                  </span>
                </button>
              ))}
            </section>
          ))}
        </div>
      )}

      {!creating ? (
        <button type="button" class="agrlist__act" onClick={() => setCreating(true)}>
          New agreement
        </button>
      ) : (
        <div class="agrlist__new">
          <select
            class="agrlist__new-field"
            value={accountId}
            disabled={busy}
            onChange={(e) => setAccountId((e.target as HTMLSelectElement).value)}
          >
            <option value="">Which account?</option>
            {accounts.map((a) => (
              <option value={a.id} key={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            class="agrlist__new-field"
            value={kind}
            disabled={busy}
            onChange={(e) => setKind((e.target as HTMLSelectElement).value)}
          >
            {AGREEMENT_KINDS.map((k) => (
              <option value={k} key={k}>{k.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <input
            class="agrlist__new-field"
            type="text"
            placeholder="Title"
            value={title}
            disabled={busy}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          />
          <input
            class="agrlist__new-field"
            type="text"
            placeholder="End condition"
            value={endCondition}
            disabled={busy}
            onInput={(e) => setEndCondition((e.target as HTMLInputElement).value)}
          />
          <select
            class="agrlist__new-field"
            value={signatureMode}
            disabled={busy}
            onChange={(e) => setSignatureMode((e.target as HTMLSelectElement).value)}
          >
            <option value="">How will they sign?</option>
            {AGREEMENT_SIGNATURE_MODES.map((m) => (
              <option value={m} key={m}>{m.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <div class="agrlist__new-actions">
            <button
              type="button"
              class="agrlist__act"
              disabled={busy || !accountId || !title.trim()}
              onClick={() => void submitNew()}
            >
              Create
            </button>
            <button type="button" class="agrlist__act" disabled={busy} onClick={() => setCreating(false)}>
              Never mind
            </button>
          </div>
        </div>
      )}
      {note ? <div class="agrlist__note">{note}</div> : null}
    </div>
  );
}
